// src/_nav.js - Navigation (i18n S1 : libellés via t(), structure inchangée)
import React from 'react'
import CIcon from '@coreui/icons-react'
import {
  cilPeople,
  cilMap,
  cilSpeedometer,
  cilUser,
  cilCalendar,
  cilBriefcase,
  cilNewspaper,
  cilHome,
  cilCommentSquare,
  cilCreditCard,
  cilWallet,
  cilLifeRing,
  cilSearch,
  cilStar,
  cilChartPie,
  cilTransfer,
  cilNotes,
  cilGift,
  cilBullhorn,
} from '@coreui/icons'
import { CNavItem } from '@coreui/react'
import './scss/_nav.scss'

// org_type pour lesquels l'item "Membres" a un libellé adapté (clé nav:members_<type>).
const KNOWN_ORG_TYPES = ['alumni', 'diaspora', 'business', 'association']

// Construit les items de navigation avec libellés internationalisés.
// `t` = fonction de traduction (react-i18next).
export const getNavItems = (t) => [
  {
    component: CNavItem,
    name: t('nav:dashboard'),
    to: '/dashboard',
    icon: (
      <CIcon icon={cilSpeedometer} customClassName="nav-icon" aria-label={t('nav:dashboard')} />
    ),
    className: 'dashboard-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:my_profile'),
    to: '/profile',
    icon: <CIcon icon={cilUser} customClassName="nav-icon" aria-label={t('nav:my_profile')} />,
    className: 'profile-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:feed'),
    to: '/publications',
    icon: <CIcon icon={cilNewspaper} customClassName="nav-icon" aria-label={t('nav:feed')} />,
    module: 'posts',
    className: 'publications-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:discussions'),
    to: '/discussions',
    icon: (
      <CIcon icon={cilCommentSquare} customClassName="nav-icon" aria-label={t('nav:discussions')} />
    ),
    module: 'discussions',
    className: 'discussions-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:members'),
    to: '/members',
    icon: <CIcon icon={cilPeople} customClassName="nav-icon" aria-label={t('nav:members')} />,
    module: 'members',
    className: 'members-nav-item',
  },
  // Placé JUSTE APRÈS l'annuaire : c'est le même besoin (« trouver quelqu'un »),
  // l'annuaire pour chercher par critères, celui-ci pour décrire un besoin.
  // Il était rangé dans « Carrière » (jargon pour un club d'affaires) et
  // n'était découvert par personne — 14 recherches lancées sur 3062 membres en
  // 90 j (rapport scripts/radar-usage-report.js, 2026-08-01).
  {
    component: CNavItem,
    name: t('nav:network_radar'),
    to: '/network-intel',
    icon: <CIcon icon={cilSearch} customClassName="nav-icon" aria-label={t('nav:network_radar')} />,
    module: 'network_radar',
    className: 'network-intel-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:map'),
    to: '/map',
    icon: <CIcon icon={cilMap} customClassName="nav-icon" aria-label={t('nav:map')} />,
    module: 'map',
    className: 'map-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:events'),
    to: '/events',
    icon: <CIcon icon={cilCalendar} customClassName="nav-icon" aria-label={t('nav:events')} />,
    module: 'events',
    className: 'events-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:career'),
    to: '/jobs',
    icon: <CIcon icon={cilBriefcase} customClassName="nav-icon" aria-label={t('nav:career')} />,
    module: 'jobs',
    className: 'jobs-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:business_referrals'),
    to: '/business-referrals',
    icon: (
      <CIcon
        icon={cilTransfer}
        customClassName="nav-icon"
        aria-label={t('nav:business_referrals')}
      />
    ),
    module: 'business_referrals',
    requiresOrgType: 'business',
    className: 'business-referrals-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:club_meetings'),
    to: '/club-meetings',
    icon: (
      <CIcon icon={cilNotes} customClassName="nav-icon" aria-label={t('nav:club_meetings')} />
    ),
    module: 'club_meetings',
    requiresOrgType: 'business',
    className: 'club-meetings-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:mentoring'),
    to: '/mentoring',
    icon: <CIcon icon={cilStar} customClassName="nav-icon" aria-label={t('nav:mentoring')} />,
    module: 'mentoring',
    className: 'mentoring-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:rcp_surveys'),
    to: '/rcp-surveys',
    icon: <CIcon icon={cilChartPie} customClassName="nav-icon" aria-label={t('nav:rcp_surveys')} />,
    module: 'rcp_surveys',
    className: 'rcp-surveys-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:accommodations'),
    to: '/accommodations',
    icon: <CIcon icon={cilHome} customClassName="nav-icon" aria-label={t('nav:accommodations')} />,
    module: 'accommodations',
    className: 'accommodations-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:campaigns'),
    to: '/campaigns',
    icon: <CIcon icon={cilBullhorn} customClassName="nav-icon" aria-label={t('nav:campaigns')} />,
    module: 'donations',
    className: 'campaigns-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:donations'),
    to: '/donations',
    icon: <CIcon icon={cilGift} customClassName="nav-icon" aria-label={t('nav:donations')} />,
    module: 'donations',
    adminOnly: true,
    className: 'donations-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:my_contribution'),
    to: '/my-contribution',
    icon: <CIcon icon={cilWallet} customClassName="nav-icon" aria-label={t('nav:my_contribution')} />,
    className: 'contribution-nav-item',
    requiresPeriods: true,
  },
  {
    component: CNavItem,
    name: t('nav:billing'),
    to: '/settings/billing',
    icon: <CIcon icon={cilCreditCard} customClassName="nav-icon" aria-label={t('nav:billing')} />,
    adminOnly: true,
    className: 'billing-nav-item',
  },
  {
    component: CNavItem,
    name: t('nav:help'),
    to: '/help',
    icon: <CIcon icon={cilLifeRing} customClassName="nav-icon" aria-label={t('nav:help')} />,
    className: 'help-nav-item',
  },
]

// Filtre + adapte la navigation. `t` injecté en 1er argument (les consommateurs
// passent useTranslation().t). Le libellé "Membres" est adapté selon org_type
// via la clé nav:members_<type>.
export const getFilteredNavigation = (
  t,
  isModuleEnabled,
  isAdmin = false,
  hasContributionPeriods = true,
  orgType = null,
) => {
  return getNavItems(t)
    .filter((item) => {
      if (item.adminOnly && !isAdmin) return false
      if (item.module && !isModuleEnabled(item.module)) return false
      if (item.requiresPeriods && !hasContributionPeriods) return false
      if (item.requiresOrgType && item.requiresOrgType !== orgType) return false
      return true
    })
    .map((item) => {
      if (item.module === 'members' && orgType && KNOWN_ORG_TYPES.includes(orgType)) {
        return { ...item, name: t(`nav:members_${orgType}`) }
      }
      return item
    })
}

export default getNavItems
